import type {
  Application,
  ApplicationEnvironment,
  ApplicationWithStats,
  UpdateApplication,
} from '@owl/shared';
import { useCallback, useState } from 'react';
import { useConfirm } from '../components/Confirm';
import type { ShellCtx } from '../components/Shell';
import { useToast } from '../components/Toast';
import { TokenRevealModal } from '../components/TokenRevealModal';
import { Icon } from '../components/icons';
import { AppHealthDot, EnvChip } from '../components/shared';
import { activateOnKey, isFromRowActions } from '../lib/activateOnKey';
import { ApiError, api } from '../lib/api';
import { computeAppHealth } from '../lib/appHealth';
import { timeAgo } from '../lib/time';
import { useAutoFocus } from '../lib/useAutoFocus';
import { useEscapeKey } from '../lib/useEscapeKey';
import { useLoader } from '../lib/useLoader';

function healthLabel(
  app: Pick<Application, 'status' | 'lastLogAt'>,
): 'ok' | 'warn' | 'down' | Application['status'] {
  const h = computeAppHealth(app);
  return h === 'idle' ? app.status : h;
}

export function Applications({ ctx }: { ctx: ShellCtx }) {
  const refreshTick = ctx.refreshTick;
  const env = ctx.environment;
  const [items, setItems] = useState<ApplicationWithStats[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<{ token: string; name: string } | null>(null);
  const [editing, setEditing] = useState<ApplicationWithStats | null>(null);
  const { push } = useToast();
  const confirm = useConfirm();

  const load = useCallback(async () => {
    try {
      const { items } = await api.listApplications(env);
      setItems(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, [env]);

  useLoader(load, refreshTick);

  async function handleStatusChange(app: ApplicationWithStats, status: Application['status']) {
    try {
      await api.updateApplicationStatus(app.id, status);
      push(`${app.name} → ${status}`);
      await load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Update failed');
    }
  }

  async function handleDelete(app: ApplicationWithStats) {
    const ok = await confirm({
      title: 'Delete application',
      sub: app.name,
      body: 'This cascades all logs and alert events for the application. It cannot be undone.',
      confirmLabel: 'Delete application',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteApplication(app.id);
      push(`${app.name} deleted`);
      await load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Delete failed');
    }
  }

  async function handleEdit(app: ApplicationWithStats, patch: UpdateApplication) {
    try {
      await api.updateApplication(app.id, patch);
      push(`${app.name} updated`);
      setEditing(null);
      await load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Update failed');
    }
  }

  async function handleRegenerate(app: ApplicationWithStats) {
    const ok = await confirm({
      title: 'Regenerate application token',
      sub: app.name,
      body: 'The current token stops working immediately. Any client still using it will start failing.',
      confirmLabel: 'Regenerate',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const { rawToken } = await api.regenerateToken(app.id);
      setRevealed({ token: rawToken, name: app.name });
      await load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Regenerate failed');
    }
  }

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div className="view-title">Applications</div>
          <div className="view-sub">{items?.length ?? 0} total</div>
        </div>
        <button type="button" className="cta" onClick={() => setCreating(true)}>
          <Icon.Plus size={13} /> New application
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {items === null ? (
        <div className="empty-state">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          No applications yet. Create one to get an ingestion token.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Application</th>
                <th>Env</th>
                <th>Status</th>
                <th>Logs 24h</th>
                <th>Error %</th>
                <th>Last log</th>
                <th>Token</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr
                  key={a.id}
                  className="data-row"
                  tabIndex={0}
                  onClick={(e) => {
                    if (isFromRowActions(e)) return;
                    ctx.navigate({ name: 'appdash', applicationId: a.id });
                  }}
                  onKeyDown={(e) =>
                    activateOnKey(e, () => ctx.navigate({ name: 'appdash', applicationId: a.id }))
                  }
                >
                  <td>
                    <div>
                      <strong>{a.name}</strong>
                    </div>
                    <div className="dim" style={{ fontSize: 11 }}>
                      {a.product} · {a.company}
                      {a.instance ? ` · ${a.instance}` : ''}
                    </div>
                  </td>
                  <td>
                    <EnvChip environment={a.environment} />
                  </td>
                  <td>
                    <AppHealthDot app={a} /> <span className="dim">{healthLabel(a)}</span>
                  </td>
                  <td className="mono">{a.logs24h}</td>
                  <td className="mono">{(a.errorRate24h * 100).toFixed(1)}%</td>
                  <td className="dim">{timeAgo(a.lastLogAt)}</td>
                  <td className="mono dim">{a.tokenPrefix}…</td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="Edit"
                        onClick={() => setEditing(a)}
                      >
                        <Icon.Edit size={12} />
                      </button>
                      {a.status === 'active' ? (
                        <button
                          type="button"
                          className="icon-btn"
                          title="Pause"
                          onClick={() => handleStatusChange(a, 'paused')}
                        >
                          <Icon.Pause size={12} />
                        </button>
                      ) : a.status === 'paused' ? (
                        <button
                          type="button"
                          className="icon-btn"
                          title="Reactivate"
                          onClick={() => handleStatusChange(a, 'active')}
                        >
                          <Icon.Play size={12} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="icon-btn"
                        title="Regenerate token"
                        onClick={() => handleRegenerate(a)}
                      >
                        <Icon.Refresh size={12} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        title="Delete"
                        onClick={() => handleDelete(a)}
                      >
                        <Icon.Trash size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateApplicationModal
          defaultEnvironment={env}
          onClose={() => setCreating(false)}
          onCreated={(name, token) => {
            setRevealed({ token, name });
            setCreating(false);
            load();
          }}
        />
      )}

      {revealed && (
        <TokenRevealModal
          token={revealed.token}
          applicationName={revealed.name}
          onClose={() => setRevealed(null)}
        />
      )}

      {editing && (
        <EditApplicationModal
          app={editing}
          onClose={() => setEditing(null)}
          onSubmit={(patch) => handleEdit(editing, patch)}
        />
      )}
    </div>
  );
}

function EditApplicationModal({
  app,
  onClose,
  onSubmit,
}: {
  app: ApplicationWithStats;
  onClose: () => void;
  onSubmit: (patch: UpdateApplication) => void | Promise<void>;
}) {
  useEscapeKey(onClose);
  const [name, setName] = useState(app.name);
  const [product, setProduct] = useState(app.product);
  const [company, setCompany] = useState(app.company);
  const [instance, setInstance] = useState(app.instance);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoFocusRef = useAutoFocus<HTMLInputElement>();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setError('Name cannot be empty');
      return;
    }
    const patch: UpdateApplication = {};
    if (trimmedName !== app.name) patch.name = trimmedName;
    if (product !== app.product) patch.product = product;
    if (company !== app.company) patch.company = company;
    if (instance !== app.instance) patch.instance = instance;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSubmit(patch);
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-card">
        <div className="modal-head">
          <div className="modal-title">Edit application</div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <Icon.X size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="modal-body">
          <label className="form-row">
            <span className="form-label">Display name</span>
            <input
              className="form-input"
              ref={autoFocusRef}
              value={name}
              maxLength={128}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <div className="hint">
              Free-form label shown in the UI. Independent from the identifiers below.
            </div>
          </label>
          <label className="form-row">
            <span className="form-label">Product</span>
            <input
              className="form-input"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder="VERA"
              required
            />
          </label>
          <label className="form-row">
            <span className="form-label">Company</span>
            <input
              className="form-input"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Mavisa"
              required
            />
          </label>
          <label className="form-row">
            <span className="form-label">Instance (optional)</span>
            <input
              className="form-input"
              value={instance}
              onChange={(e) => setInstance(e.target.value)}
              placeholder="Sabadell"
            />
            <div className="hint">Sede, ciudad o despliegue. Deja vacío si no aplica.</div>
          </label>
          {error && (
            <div className="error-banner" style={{ marginBottom: 0 }}>
              {error}
            </div>
          )}
          <div className="modal-foot" style={{ padding: 0, border: 0 }}>
            <button type="button" className="cta-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cta" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateApplicationModal({
  defaultEnvironment,
  onClose,
  onCreated,
}: {
  defaultEnvironment: ApplicationEnvironment;
  onClose: () => void;
  onCreated: (name: string, token: string) => void;
}) {
  useEscapeKey(onClose);
  const [product, setProduct] = useState('');
  const [company, setCompany] = useState('');
  const [instance, setInstance] = useState('');
  const [environment, setEnvironment] = useState<ApplicationEnvironment>(defaultEnvironment);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoFocusRef = useAutoFocus<HTMLInputElement>();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { application, rawToken } = await api.createApplication({
        product,
        company,
        instance,
        environment,
      });
      onCreated(application.name, rawToken);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  const resultingName =
    product && company
      ? instance
        ? `${product}-${company}-${instance}`
        : `${product}-${company}`
      : '—';

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-card">
        <div className="modal-head">
          <div className="modal-title">New application</div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <Icon.X size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="modal-body">
          <label className="form-row">
            <span className="form-label">Product</span>
            <input
              className="form-input"
              ref={autoFocusRef}
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder="VERA"
              required
            />
          </label>
          <label className="form-row">
            <span className="form-label">Company</span>
            <input
              className="form-input"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Mavisa"
              required
            />
          </label>
          <label className="form-row">
            <span className="form-label">Instance (optional)</span>
            <input
              className="form-input"
              value={instance}
              onChange={(e) => setInstance(e.target.value)}
              placeholder="Sabadell"
            />
            <div className="hint">Sede, ciudad o despliegue. Deja vacío si no aplica.</div>
          </label>
          <fieldset className="form-row">
            <legend className="form-label">Environment</legend>
            <div className="range-picker" style={{ width: 'fit-content' }}>
              <button
                type="button"
                className={`range-opt ${environment === 'development' ? 'active' : ''}`}
                onClick={() => setEnvironment('development')}
              >
                Development
              </button>
              <button
                type="button"
                className={`range-opt ${environment === 'production' ? 'active' : ''}`}
                onClick={() => setEnvironment('production')}
              >
                Production
              </button>
            </div>
            <div className="hint">El token estará ligado a este environment.</div>
          </fieldset>
          <div className="hint">Resulting name: {resultingName}</div>
          {error && (
            <div className="error-banner" style={{ marginBottom: 0 }}>
              {error}
            </div>
          )}
          <div className="modal-foot" style={{ padding: 0, border: 0 }}>
            <button type="button" className="cta-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cta" disabled={busy}>
              {busy ? 'Creating…' : 'Create application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
